import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import React from "react";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/components/AuthProvider";
import { AuthenticatedApp } from "@/components/AuthenticatedApp";
import { useAnalytics } from "@/hooks/useAnalytics";
import { prewarmStockfish } from "@/lib/stockfish-engine";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ChallengeNotifications } from "@/components/ChallengeNotifications";

// Page imports
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import AvatarEditor from "./pages/AvatarEditor";
import Analysis from "./pages/Analysis";
import Leaderboards from "./pages/Leaderboards";
import PlayerSearch from "./pages/PlayerSearch";
import PlayerProfile from "./pages/PlayerProfile";
import SecurityDashboard from "./pages/SecurityDashboard";
import SecuritySettings from "./pages/SecuritySettings";
import ConfirmLogin from "./pages/ConfirmLogin";
import SecureAccount from "./pages/SecureAccount";

import Terms from "./pages/Terms";
import Forums from "./pages/Forums";
import ForumCategory from "./pages/ForumCategory";
import ForumTopic from "./pages/ForumTopic";
import PuzzlesPage from "./pages/Puzzles";
import TutorialsPage from "./pages/Tutorials";
import TutorialManagerPage from "./pages/TutorialManager";
import TutorialValidationPage from './pages/TutorialValidation';
import ValidationWizardPage from './pages/ValidationWizardPage';
import AdminPage from "./pages/Admin";
import AdminUsersPage from "./pages/AdminUsers";
import AdminAnalyticsPage from "./pages/AdminAnalytics";
import AdminBehaviorPage from "./pages/AdminBehavior";
import AdminSettingsPage from "./pages/AdminSettings";
import AdminSecurityPage from "./pages/AdminSecurity";
import { AdminPuzzleManager } from "./components/admin/AdminPuzzleManager";
import NotFound from "./pages/NotFound";
import UserAgreement from "./pages/UserAgreement";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import About from "./pages/About";
import FairPlayPolicy from "./pages/FairPlayPolicy";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Sitemap from "./pages/Sitemap";
import HatchRules from "./pages/HatchRules";
import HowItWorks from "./pages/HowItWorks";
import Training from "./pages/Training";
import TrainingStats from "./pages/TrainingStats";
import PlayOnline from "./pages/PlayOnline";

const queryClient = new QueryClient();

// Analytics wrapper component
function AnalyticsWrapper({ children }: { children: React.ReactNode }) {
  useAnalytics();
  
  // Prewarm Stockfish on app startup
  React.useEffect(() => {
    prewarmStockfish().catch(console.warn);
  }, []);
  
  return <>{children}</>;
}

const App = () => (
  <ErrorBoundary>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AuthenticatedApp>
            <TooltipProvider>
              <Toaster />
              <Sonner />
            <BrowserRouter>
              <ChallengeNotifications />
              <AnalyticsWrapper>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/profile/avatar" element={<AvatarEditor />} />
                  <Route path="/analysis" element={<Analysis />} />
                  <Route path="/training" element={<Training />} />
                  <Route path="/training/stats" element={<TrainingStats />} />
                  <Route path="/play-online" element={<PlayOnline />} />
                  <Route path="/leaderboards" element={<Leaderboards />} />
                  <Route path="/security/dashboard" element={<SecurityDashboard />} />
                  <Route path="/security/settings" element={<SecuritySettings />} />
                  <Route path="/security/confirm-login" element={<ConfirmLogin />} />
                  <Route path="/security/secure-account" element={<SecureAccount />} />
                  <Route path="/players" element={<PlayerSearch />} />
                  <Route path="/player/:userId" element={<PlayerProfile />} />
                  <Route path="/forums" element={<Forums />} />
                  <Route path="/forums/c/:slug" element={<ForumCategory />} />
                  <Route path="/forums/t/:id" element={<ForumTopic />} />
                  {/* Hidden routes - tutorials and puzzles disabled */}
                  {/* <Route path="/puzzles" element={<PuzzlesPage />} /> */}
                  {/* <Route path="/tutorials" element={<TutorialsPage />} /> */}
                  {/* <Route path="/admin/tutorials" element={<TutorialManagerPage />} /> */}
                  {/* <Route path="/admin/tutorial-validation" element={<TutorialValidationPage />} /> */}
                  {/* <Route path="/admin/validation-wizard" element={<ValidationWizardPage />} /> */}
                  {/* <Route path="/admin/puzzles" element={<AdminPuzzleManager />} /> */}
                  <Route path="/admin/users" element={<AdminUsersPage />} />
                  <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
                  <Route path="/admin/behavior" element={<AdminBehaviorPage />} />
                  <Route path="/admin/settings" element={<AdminSettingsPage />} />
                  <Route path="/admin/security" element={<AdminSecurityPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/user-agreement" element={<UserAgreement />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/fair-play-policy" element={<FairPlayPolicy />} />
                  <Route path="/sitemap" element={<Sitemap />} />
                  <Route path="/hatch-rules" element={<HatchRules />} />
                  <Route path="/how-it-works" element={<HowItWorks />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </AnalyticsWrapper>
            </BrowserRouter>
          </TooltipProvider>
          </AuthenticatedApp>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;